import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { TranslateService } from '@app/core/services/translate.service';

@Component({
    selector: 'tt-gantt-button',
    standalone: false,
    templateUrl: './gantt-button.component.html',
    styleUrls: ['./gantt-button.component.css'],
})
export class GanttButtonComponent implements OnChanges {
    @Input() public ttText = '';
    @Input() public ttTooltipText = '';
    @Input() public ttIcon = '';
    @Input() public ttDisabledText: string | null = null;
    @Input() public ttDisabledTooltipText: string | null = null;
    @Input() public ttDisabledIcon: string | null = null;
    @Input() public ttLoadingText: string | null = null;
    @Output() public ttClick = new EventEmitter<MouseEvent>();

    @Input()
    public get ttDisabled(): boolean { return this._disabled; }
    public set ttDisabled(value: BooleanInput) { this._disabled = coerceBooleanProperty(value); }
    private _disabled = false;

    @Input()
    public get ttLoading(): boolean { return this._loading; }
    public set ttLoading(value: BooleanInput) { this._loading = coerceBooleanProperty(value); }
    private _loading = false;

    @Input()
    public get ttTranslateText(): boolean { return this._translateText; }
    public set ttTranslateText(value: BooleanInput) { this._translateText = coerceBooleanProperty(value); }
    private _translateText = true;

    @Input()
    public get ttTranslateTooltipText(): boolean { return this._translateTooltipText; }
    public set ttTranslateTooltipText(value: BooleanInput) { this._translateTooltipText = coerceBooleanProperty(value); }
    private _translateTooltipText = true;

    @Input()
    public get ttTranslateDisabledText(): boolean { return this._translateDisabledText; }
    public set ttTranslateDisabledText(value: BooleanInput) { this._translateDisabledText = coerceBooleanProperty(value); }
    private _translateDisabledText = true;

    @Input()
    public get ttTranslateDisabledTooltipText(): boolean { return this._translateDisabledTooltipText; }
    public set ttTranslateDisabledTooltipText(value: BooleanInput) { this._translateDisabledTooltipText = coerceBooleanProperty(value); }
    private _translateDisabledTooltipText = true;

    @Input()
    public get ttTranslateLoadingText(): boolean { return this._translateLoadingText; }
    public set ttTranslateLoadingText(value: BooleanInput) { this._translateLoadingText = coerceBooleanProperty(value); }
    private _translateLoadingText = true;

    public text = '';
    public tooltipText = '';
    public icon = '';
    public disabledText: string | null = null;
    public disabledTooltipText: string | null = null;
    public disabledIcon: string | null = null;
    public loadingText: string | null = null;
    private translationRevision = 0;

    public get displayText(): string {
        if (this.ttLoading) return this.loadingText ?? this.text;
        return this.ttDisabled ? this.disabledText ?? this.text : this.text;
    }

    public get displayTooltipText(): string {
        return this.ttDisabled && !this.ttLoading ? this.disabledTooltipText ?? this.tooltipText : this.tooltipText;
    }

    public get displayIcon(): string {
        if (this.ttLoading) return 'fa-fw fas fa-spin fa-spinner-third';
        return this.ttDisabled ? this.disabledIcon ?? this.icon : this.icon;
    }

    constructor(private translate: TranslateService) {}

    public click(event: MouseEvent): void {
        if (!this.ttDisabled && !this.ttLoading) this.ttClick.emit(event);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttIcon']) this.icon = this.formatIcon(this.ttIcon);
        if (changes['ttDisabledIcon']) this.disabledIcon = this.ttDisabledIcon === null ? null : this.formatIcon(this.ttDisabledIcon);
        if (['ttText', 'ttTooltipText', 'ttDisabledText', 'ttDisabledTooltipText', 'ttLoadingText',
            'ttTranslateText', 'ttTranslateTooltipText', 'ttTranslateDisabledText',
            'ttTranslateDisabledTooltipText', 'ttTranslateLoadingText'].some(key => changes[key])) {
            void this.setLabels();
        }
    }

    private formatIcon(icon: string): string {
        if (!icon || icon.includes(' ')) return icon;
        const fontAwesome = /^(fa[a-z]?)-(.+)$/.exec(icon);
        return fontAwesome ? `${fontAwesome[1]} fa-${fontAwesome[2]}` : `glyphicon ${icon}`;
    }

    private async translateLabel(value: string | null, translate: boolean): Promise<string | null> {
        return value && translate ? this.translate.translate(value) : value;
    }

    private async setLabels(): Promise<void> {
        const revision = ++this.translationRevision;
        this.text = this.ttText;
        this.tooltipText = this.ttTooltipText;
        this.disabledText = this.ttDisabledText;
        this.disabledTooltipText = this.ttDisabledTooltipText;
        this.loadingText = this.ttLoadingText;
        const [text, tooltip, disabledText, disabledTooltip, loadingText] = await Promise.all([
            this.translateLabel(this.ttText, this.ttTranslateText),
            this.translateLabel(this.ttTooltipText, this.ttTranslateTooltipText),
            this.translateLabel(this.ttDisabledText, this.ttTranslateDisabledText),
            this.translateLabel(this.ttDisabledTooltipText, this.ttTranslateDisabledTooltipText),
            this.translateLabel(this.ttLoadingText, this.ttTranslateLoadingText),
        ]);
        if (revision !== this.translationRevision) return;
        this.text = text ?? '';
        this.tooltipText = tooltip ?? '';
        this.disabledText = disabledText;
        this.disabledTooltipText = disabledTooltip;
        this.loadingText = loadingText;
    }
}
